/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { AuthService, EventQueueService } from "../../../services";
import { User, Error, AppEvent } from "../../../models";
import { ToastrService } from "ngx-toastr";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { finalize } from "rxjs/operators";
import { AppEventType } from "../../../app-event-type.enum";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html"
})
export class LoginComponent implements OnInit {
  return = "";
  applicationName = "Medfusion Merchant Provisioning";
  loginForm!: FormGroup;
  error = "";
  faExclamationTriangle = faExclamationTriangle;
  loggingIn = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private toast: ToastrService,
    private eventQueue: EventQueueService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => (this.return = params["return"] || "/home"));
    this.loginForm = this.fb.group({
      loginName: ["", Validators.required],
      loginPassword: ["", Validators.required]
    });
  }

  login(): void {
    if (this.loginForm.valid) {
      this.loggingIn = true;
      this.cleanErrorMessage();

      this.authService
        .login(this.loginForm.getRawValue().loginName, this.loginForm.getRawValue().loginPassword)
        .pipe(
          finalize(() => {
            this.loggingIn = false;
          })
        )
        .subscribe(
          (user: User) => {
            if (user.roles.length === 1 && user.roles[0] === "ANONYMOUS") {
              this.toast.error("You are logged in with Anonymous permissions", "Permissions Warning:");
            }
            // login successful -> navigate to dashboard
            this.eventQueue.dispatch(new AppEvent(AppEventType.Login));
            this.eventQueue.dispatch(new AppEvent(AppEventType.menuCollapse));
            this.router.navigateByUrl(this.return);
          },
          (error) => {
            // login problem, wrong user credentials or another REST problem
            this.showErrorMessage(error);
          }
        );
    } else {
      // manually dirty login form elements to display error messages
      // as we manually omit them for newly displayed (and empty) login form
      // see ngMessages section within login form HTML view
      this.loginForm.controls["loginName"].markAsDirty();
      this.loginForm.controls["loginPassword"].markAsDirty();
    }
  }

  private showErrorMessage(error: Error) {
    let message = "";
    switch (error.status) {
      case 401:
      case 403:
        message = "Provided credentials are not valid";
        break;
      default:
        message = "Server error. Please try again later";
    }

    this.error = message;
  }

  cleanErrorMessage(): void {
    this.error = "";
  }

  get loginName() {
    return this.loginForm.controls["loginName"];
  }
  get loginPassword() {
    return this.loginForm.controls["loginPassword"];
  }
}
